import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Alert,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import * as yup from "yup";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { TipologiaAssociazioni } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import { getString } from "../utils/getString";

type Inputs = {
  id?: number;
  denominazione: string | null;
  esenzionePagamentoOre: boolean;
  sforamentoFidoConsentito: boolean;
  condizioni: string | null;
  visibilitaGiorni: boolean;
  prenotazioneMultipla: boolean;
};
const defaultValues = {
  denominazione: "",
  esenzionePagamentoOre: false,
  sforamentoFidoConsentito: false,
  condizioni: "",
};
const schema = yup
  .object({
    // esenzionePagamentoOre: yup.boolean(),
    sforamentoFidoConsentito: yup.boolean(),
    visibilitaGiorni: yup.boolean(),
  })
  .required();

export function TipologiaAssociazione(props: { id?: number; closeDialog: () => void }) {
  let { id, closeDialog } = props;

  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { refetch } = useQuery<{ tipologia: TipologiaAssociazioni }, AxiosError>(
    ["tipologie-associazioni", id],
    () => axios.get(API_URL + "tipologie-associazioni/get/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        const { tipologia } = data;
        setLoaded(true);

        setValue("denominazione", getString(tipologia.denominazione));
        setValue("condizioni", getString(tipologia.condizioni));
        setValue("sforamentoFidoConsentito", tipologia.sforamentoFidoConsentito);
        setValue("visibilitaGiorni", tipologia.visibilitaGiorni);
        setValue("prenotazioneMultipla", tipologia.prenotazioneMultipla);
      },
      onError: (error: AxiosError) => {
        console.error(error);
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error, mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tipologie-associazioni/create", data);
    },
    {
      onSuccess: (data) => {
        const { id } = data.data;
        if (id) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Tipologia Associazione Creata", { variant: "success" });
          closeDialog();
        }
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.patch(API_URL + "tipologie-associazioni/" + id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Tipologia Associazione Aggiornata", { variant: "success" });
        closeDialog();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    if (id !== undefined) mutateUpdate(data);
    else mutate({ ...data });
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  let errorMsg = error?.response?.data?.error ?? "";

  let title = id === undefined ? "Crea Tipologia Associazione" : "Modifica Tipologia Associazione";

  if (!loaded)
    return (
      <Box minHeight={"220px"}>
        <DialogTitle>{title}</DialogTitle>
        <Loading />
      </Box>
    );

  return (
    <Box className={animations ? "" : "noAnimation"}>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container columnSpacing={1}>
            <Grid item xs={12}>
              <Controller
                name="denominazione"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    variant="standard"
                    label="Denominazione"
                    error={Boolean(errors.denominazione)}
                    helperText={errors.denominazione?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="condizioni"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Condizioni"
                    error={Boolean(errors.condizioni)}
                    helperText={errors.condizioni?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="sforamentoFidoConsentito"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("sforamentoFidoConsentito", v.target.checked)}
                      />
                    }
                    label="Sforamento Fido Consentito"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="visibilitaGiorni"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("visibilitaGiorni", v.target.checked)}
                      />
                    }
                    label="Abilità visibilita giorni"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="prenotazioneMultipla"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("prenotazioneMultipla", v.target.checked)}
                      />
                    }
                    label="Abilità ripetizione prenotazioni"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button sx={{ mt: 2 }} onClick={closeDialog}>
            Chiudi
          </Button>

          {!id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Crea Tipologia
            </Button>
          )}
          {id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Modifica Tipologia
            </Button>
          )}
        </DialogActions>
      </form>
      {errorMsg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMsg}
        </Alert>
      )}
    </Box>
  );
}
